import FasesSection from '../../sections/FasesSection';
import Descargas from '../../components/Descargas';
import { DocumentRepository2 } from '../../components/DocumentRepository';
import { motion, AnimatePresence } from "framer-motion";
import { FaFilePdf, FaProjectDiagram, FaLightbulb, FaClipboardList, FaMap } from "react-icons/fa";

import './Tapalpa.css';
import React, { useState, useEffect } from 'react';
import { 
  ChevronRight,
  Users,
  Map,
  Landmark,
  Calendar,
  MountainSnow,
  MapPin,
  Waves,
  Info,
  FileSignature,
  FileSpreadsheet,
  BarChart3,
  Zap,
  FileText,
  ArrowDown,
  Compass,
  GlobeIcon,
  DownloadCloud,
  CheckCircle,
  ExternalLink,
  Layers,
  Target,
  Trees,
} from 'lucide-react';

const DetailBadge = ({ icon: Icon, label, value }) => (
  <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl transition-all hover:bg-teal-100 dark:hover:bg-teal-900/30">
    <Icon className="text-teal-600 dark:text-teal-400 mr-3 flex-shrink-0" size={20} />
    <div className="text-sm">
      <span className="font-semibold text-gray-700 dark:text-gray-300 block leading-tight">{label}:</span>
      <span className="text-gray-900 dark:text-white font-bold">{value}</span>
    </div>
  </div>
);

const StatusBadge = () => {
  return (
    <motion.div 
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/20 to-teal-500/20 backdrop-blur-md border border-emerald-500/30"
    >
      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse mr-2" />
      <span className="text-sm font-medium text-white tracking-wider">FASE </span>
      <div className="ml-2 pl-2 border-l border-white/20">
        <span className="text-xs text-white/70">En Ejecución</span>
      </div>
    </motion.div>
  );
};

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const offset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

const ModernBanner = ({ onScrollClick }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const stats = [
    { value: '4', label: 'Municipios', icon: MapPin },
    { value: '2,020', label: 'km²', icon: Map },
    { value: '2019', label: 'Inicio', icon: Calendar },
    { value: 'Vigente', label: 'Estado', icon: CheckCircle },
  ];

  return (
    <section className="relative h-screen min-h-[780px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="images/Foto-Tapalpa.jpg"
          alt="Tapalpa"
          className="w-full h-full object-cover"
          style={{
            transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.01}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-slate-950/60 to-slate-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/20 via-transparent to-slate-950/10" />
      </div>

      <div className="relative z-10 h-full flex items-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-6">
                <StatusBadge />
              </div>
              <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
                <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">Región Tapalpa</span>
              </h1>
              <p className="text-xl text-gray-200 mb-8 max-w-xl">
                Ordenamiento territorial y desarrollo sustentable en los municipios de Atemajac de Brizuela, Chiquilistlán, San Gabriel y Tapalpa.
              </p>
              <div className="flex flex-wrap gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  onClick={() => onScrollClick('introduccion')}
                  className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-2xl transition-all"
                >
                  Explorar Región
                </motion.button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:grid grid-cols-2 gap-4"
            >
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="text-center">
                    <Icon className="mx-auto mb-2 text-cyan-200" size={24} />
                    <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-xs text-white/70 tracking-wide">{stat.label}</div>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 3, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
      >
        <ArrowDown className="text-cyan-400" size={32} />
      </motion.div>
    </section>
  );
};

const ModernBitacoraSection = ({ items }) => {
  const [expandedSection, setExpandedSection] = useState(0);

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Bitácora del Programa
            </span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Registro documental completo del proceso de ordenamiento ecológico
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {items.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.button
                key={index}
                onClick={() => setExpandedSection(expandedSection === index ? null : index)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`p-6 rounded-2xl transition-all duration-300 text-left ${
                  expandedSection === index
                    ? `bg-gradient-to-br ${item.color} text-white shadow-2xl`
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-lg hover:shadow-xl'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-xl ${
                    expandedSection === index
                      ? 'bg-white/20'
                      : `bg-gradient-to-br ${item.color}`
                  }`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold">{item.title}</h3>
                </div>
                <p className={`text-sm ${
                  expandedSection === index ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {expandedSection === index ? 'Ver documentos ↓' : 'Ver documentos →'}
                </p>
              </motion.button>
            );
          })}
        </div>

        <AnimatePresence>
          {expandedSection !== null && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-gray-700">
                {items[expandedSection].content.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="mb-8 last:mb-0">
                    <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                      <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full mr-3" />
                      {section.subtitle}
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {section.items.map((doc, docIndex) => (
                        <a
                          key={docIndex}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-teal-50 
                            dark:hover:from-blue-900/20 dark:hover:to-teal-900/20 transition-all duration-300"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-blue-500 group-hover:text-teal-500 transition-colors" />
                            <span className="text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                              {doc.text}
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
                
                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                    {items[expandedSection].content.reduce((total, section) => total + section.items.length, 0)} documentos disponibles
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const FullWidthVisorWithSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('uga');

  return (
    <section id="visor-completo" className="py-16">
      <div className="max-w-[2000px] mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            <span className="bg-gradient-to-r from-cyan-600 to-emerald-600 bg-clip-text text-transparent">
              Plataforma Geoespacial Completa
            </span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Sistema de información geográfica integrado con documentación técnica completa para la región Tapalpa.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className={`lg:w-96 ${sidebarOpen ? 'block' : 'hidden lg:block'}`}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 sticky top-6">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden mb-4 w-full p-2 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white rounded-lg flex items-center justify-center gap-2"
              >
                {sidebarOpen ? 'Ocultar Panel' : 'Mostrar Panel'}
                <ChevronRight className={`w-4 h-4 transition-transform ${sidebarOpen ? 'rotate-90' : ''}`} />
              </button>

              <div className="flex mb-6 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('uga')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'uga'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Clasificación UGA
                </button>
                <button
                  onClick={() => setActiveTab('descripcion')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'descripcion'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Descripción UGA
                </button>
                <button
                  onClick={() => setActiveTab('politica')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'politica'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Política Ambiental
                </button>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  {activeTab === 'uga' && (
                    <>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                          <Layers className="w-5 h-5 text-blue-500 mr-2" />
                          Clasificación de UGAs
                        </h3>
                        <img
                          src="/images/clave_uga_tapalpa.png"
                          alt="Clasificación de Unidades de Gestión Ambiental"
                          className="w-full h-auto rounded-lg shadow-md mb-4"
                        />
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Mapa de clasificación de las Unidades de Gestión Ambiental (UGAs) de la región Tapalpa.
                        </p>
                      </div>
                    </>
                  )}

                  {activeTab === 'descripcion' && (
                    <>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                          <Info className="w-5 h-5 text-teal-500 mr-2" />
                          Descripción de las UGAs
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          Las Unidades de Gestión Ambiental son la unidad mínima del territorio a la que se asignan lineamientos y estrategias ecológicas para su manejo específico.
                        </p>
                        <div className="space-y-4">
                          <div className="bg-gradient-to-r from-cyan-50 to-emerald-50 dark:from-cyan-900/20 dark:to-emerald-900/20 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Características</h4>
                            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                              <li>• Unidad territorial homogénea</li>
                              <li>• Criterios específicos de manejo</li>
                              <li>• Estrategias ecológicas definidas</li>
                              <li>• Monitoreo individualizado</li>
                            </ul>
                          </div>
                          <div className="bg-gradient-to-r from-emerald-50 to-lime-50 dark:from-emerald-900/20 dark:to-lime-900/20 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Objetivos</h4>
                            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                              <li>• Gestión ambiental específica</li>
                              <li>• Aplicación de lineamientos</li>
                              <li>• Evaluación de resultados</li>
                              <li>• Adaptación de estrategias</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {activeTab === 'politica' && (
                    <>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                          <Target className="w-5 h-5 text-purple-500 mr-2" />
                          Política Ambiental
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                          El modelo de ordenamiento considera una zonificación integrada por políticas orientadas a la conservación, el uso responsable y la restauración del territorio.
                        </p>
                      </div>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>

          <div className="flex-1">
            <div className="bg-white dark:bg-gray-900/90 rounded-3xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
              <iframe
                title="Visor Tapalpa"
                src="https://siga.jalisco.gob.mx/ordenamiento/visores/visorTapalpa/index.html"
                className="w-full min-h-[720px]"
                frameBorder="0"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const items = [
  {
    title: "Archivos Vectoriales",
    description: "Archivos geoespaciales para la consulta SIG.",
    imageUrl: "images/vectoriales.png", // sky-600
    baseColor: "sky",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Descargas/VectorialesTapalpa.zip",

    target: "_blank",
  },
  {
    title: "Fichas UGAss",
    description: "Informacion detallada de cada Unidad de Gestión Ambiental (UGA).",
    imageUrl: "images/FICHAS_UGA.png", // green-600
    baseColor: "green",
    
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Descargas/Fichas%20de%20las%20Unidades%20de%20Gesti%C3%B3n%20Ambiental%20Regi%C3%B3n%20Tapalpa.pdf",
    target: "_blank",
  },
  {
    title: "Criterios",
    description: "Regulaciones ecológicas para la preservación y restauración del territorio.",
    imageUrl: "images/Criterios.png", // orange-600
    baseColor: "orange",
    
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Descargas/Criterios%20Regi%C3%B3n%20Tapalpa.pdf",
    target: "_blank",
  },
  {
    title: "Estrategias Ecologicas",
    description: "Proyectos de orientación para la protección del entorno natural.",
    imageUrl: "images/Estrategias.png", // red-600
    baseColor: "red",
    
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Descargas/Estrategias%20Regi%C3%B3n%20Tapalpa.pdf",

    target: "_blank",
  },
  {
    title: "Lineamientos",
    description: "Conservar, proteger y restaurar los recursos naturales de la región.",
    imageUrl: "images/Lineamientos.png", // indigo-600
    baseColor: "indigo",    
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Descargas/Lineamientos%20ecol%C3%B3gicos%20Regi%C3%B3n%20Tapalpa.pdf",
    target: "_blank",
  },
  {
    title: "Compatibilidades",
    description: "Guia de compatibilidad de  usos del suelo estratégicos.",
    imageUrl: "images/Compatibilidades.png", // teal-600
    baseColor: "teal",
    
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Descargas/Listado%20de%20usos%20y%20actividades%20sujetos%20a%20controles%20ambientales%20Regi%C3%B3n%20Tapalpa.pdf",
    target: "_blank",
  },
];
const Items = [
  {
    title: "Archivos Vectoriales",
    description: "Archivos geoespaciales para consulta y análisis del SIG.",
    imageUrl: "https://placehold.co/64x64/0284c7/ffffff?text=MAP",
    baseColor: "sky",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Descargas/VectorialesTapalpa.zip",
    target: "_blank",
  },
  {
    title: "Fichas UGAs",
    description: "Información detallada sobre las Unidades de Gestión Ambiental.",
    imageUrl: "https://placehold.co/64x64/059669/ffffff?text=LIST",
    baseColor: "green",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Descargas/Fichas%20de%20las%20Unidades%20de%20Gesti%C3%B3n%20Ambiental%20Regi%C3%B3n%20Tapalpa.pdf",
    target: "_blank",
  },
  {
    title: "Criterios",
    description: "Regulaciones ecológicas para la preservación y restauración del territorio.",
    img: <img src="images/Criterios.png" alt="Criterios" className="w-16 h-16 mb-4" />,
    icon: <FaFilePdf className="text-5xl text-white" />,
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Descargas/Criterios%20Regi%C3%B3n%20Tapalpa.pdf",
  },
  {
    title: "Estrategias Ecologicas",
    description: "Proyectos de orientación para la protección del entorno.",
    img: <img src="images/Estrategias.png" alt="Estrategias" className="w-16 h-16 mb-4" />,
    icon: <FaProjectDiagram className="text-5xl text-white" />,
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Descargas/Estrategias%20Regi%C3%B3n%20Tapalpa.pdf",
  },
  {
    title: "Lineamientos",
    description: "Conservar, proteger y restaurar los ecosistemas y recursos naturales.",
    img: <img src="images/Lineamientos.png" alt="Lineamientos" className="w-16 h-16 mb-4" />,
    icon: <FaLightbulb className="text-5xl text-white" />,
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Descargas/Lineamientos%20ecol%C3%B3gicos%20Regi%C3%B3n%20Tapalpa.pdf",
  },
];

const TapalpaMain = items.map((item, index) => ({
  id: `item-${index}`,
  title: item.title,
  subtitle: item.description,
  category: 'Documento',
  url: item.link
}));

const imageList = [
    {
        id: '1',
        src: 'images/cabo-corrientes-escudo.png',
        thumbnail: 'images/cabo-corrientes-escudo.png',
        alt: 'Paisaje con montañas',
        caption: 'Cabo Corrientes.'
    },
    {
        id: '2',
        src: 'images/tomatlan-escudo.png',
        thumbnail: 'images/tomatlan-escudo.png',
        alt: 'Ciudad nocturna',
        caption: 'Tomatlán.'
    },
    {
        id: '3',
        src: 'images/la_huerta-escudo.png',
        thumbnail: 'images/la_huerta-escudo.png',
        alt: 'Bosque en otoño',
        caption: 'La Huerta.'
    },
    {
        id: '4',
        src: 'images/cihuatlan-escudo.png',
        thumbnail: 'images/cihuatlan-escudo.png',
        alt: 'Playa tropical',
        caption: 'Cihuatlán.'
    },
    
];

// Datos de los municipios
const municipalities = [
  {
    id: 1,
    name: 'Atemajac de Brizuela',
    shield: 'images/Logo_Atemajac_de_brizuela.png',
    description: 'Con agricultura de temporal enfocada en maíz y frijol, y ganadería extensiva, el municipio se ubica en valles y lomeríos con suelos volcánicos y arroyos estacionales. Su entorno natural favorece la observación de flora y fauna local.',
    population: '7,758 habitantes',
    area: '355.8 km²',
    foundation: '1884',
    attractions: 'Las Presitas, la Sierra de Atemajac y el Mirador de la Piedra Redonda conforman paisajes ideales para senderismo, recorridos rurales y observación natural. El municipio destaca por sus bosques templados, así como por actividades comunitarias relacionadas con la producción agrícola y la gastronomía local.',
    color: 'from-blue-500 to-teal-600',
    administrativeRegion: 'Lagunas',
    NumerodeUgas: '7 UGAs',
    
  },
  {
    id: 2,
    name: 'Chiquilistlán',
    shield: 'images/Chiliquistlán.png',
    description: 'Destacado por la agricultura de temporal y la ganadería menor, se localiza en áreas montañosas con bosques de pino–encino y cañadas profundas. Sus ecosistemas permiten el ecoturismo rural, senderismo y la aplicación de prácticas locales de conservación forestal',
    population: '5,983 habitantes',
    area: '297.4 km²',
    foundation: '1857',    
    attractions: 'Las Cascadas y rios del Cañón de Comala y los balnearios de aguas termales son puntos destacados para disfrutar de naturaleza y caminatas. Sus paisajes serranos permiten la observación de flora y fauna, además de recorridos por zonas agrícolas y ganaderas que reflejan las tradiciones locales.',
    color: 'from-green-500 to-emerald-600',
    administrativeRegion: 'Sierra de Mula',
    NumerodeUgas: '14 UGAs'
  },
  {
    id: 3,
    name: 'San Gabriel',
    shield: 'images/San_Gabriel.png',
    description: 'Con producción agrícola y ganadera, el municipio se desarrolla en lomeríos y valles con suelos fértiles y presencia de ríos temporales. Sus áreas naturales facilitan la observación de flora y fauna.',
    population: '16,548 habitantes',
    area: '746.1 km²',
    foundation: '1848',
    attractions: 'Las barrancas, miradores naturales y ríos que descienden desde la Sierra de Tapalpa conforman paisajes serranos ideales para el senderismo, recorridos rurales y la observación de flora y fauna. Destacan sitios como Las Piedrotas  junto con su centro histórico y templos tradicionales.',
    color: 'from-amber-500 to-orange-600',
    administrativeRegion: 'Sur',
    EcologicaZona: 'Área Natural Protegida Parque Estatal Bosque Mesófilo Nevado de Colima y Parque Nacional Volcán Nevado de Colima.',
    NumerodeUgas: '20 UGAs'
  },
  {
    id: 4,
    name: 'Tapalpa',
    shield: 'images/Tapalpa.png',
    description: 'Reconocido por la agricultura de temporal y ganadería de pequeña escala, se encuentra en un relieve montañoso con bosques de pino–encino y ríos permanentes. Su territorio permite el desarrollo de ecoturismo de montaña y la ejecución de programas de conservación forestal',
    population: '21,245 habitantes',
    area: '619.4  km²',
    foundation: '1869',
    attractions: ' Destaca por sus bosques de pino y encino, las formaciones rocosas de Las Piedrotas y la cascada El Salto del Nogal, cabañas inmersas en paisajes serranos. Sus paisajes serranos permiten senderismo, observación natural y recorridos por zonas rurales de alto valor ecológico.',
    color: 'from-purple-500 to-indigo-600',
    administrativeRegion: 'Lagunas',
    NumerodeUgas: '15 UGAs'
  }
];

const TapalpaPage = () => {
  const [selectedMunicipality, setSelectedMunicipality] = useState(municipalities[0]);
  const aboutContent = `
    
  <h2 class="text-2xl font-bold mb-4">Ordenamiento Región Tapalpa</h2>
 
    <p>El Gobierno del Estado de Jalisco impulsa el desarrollo sustentable en la región denominada “Tapalpa”, integrada por los municipios de Atemajac de Brizuela, Chiquilistlán , San Gabriel y Tapalpa. Para impulsar dicho desarrollo, el día 15 de junio del año 2019, el Gobierno del Estado de Jalisco y los municipios firmaron un Convenio de Coordinación con el objetivo de realizar los instrumentos necesarios para ordenar el territorio de la región. Asimismo, se firmó con la Federación un Convenio Marco de Coordinación de fecha 27 de enero de 2020 y derivado del mismo un Convenio de Coordinación Específico (SEMARNAT-CONANP y SEDATU) de fecha 27 de mayo 2020. Estos convenios establecen los fundamentos para la colaboración en el proceso de elaboración de los siguientes instrumentos de ordenamiento territorial:
    </p>
    <br>
    
  `;

  const aboutLinks = [
    
    { text: 'Programa de Ordenamiento Ecológico y Territorial Regional', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Descargas/Programa%20de%20Ordenamiento%20Ecol%C3%B3gico%20Regi%C3%B3n%20Tapalpa.pdf' },
    { text: 'Plan Regional de Integración Urbana', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Descargas/Plan%20Regional%20de%20Integraci%C3%B3n%20Urbana%20Regi%C3%B3n%20Tapalpa.pdf' },
    { text: 'Programas Municipales de Desarrollo Urbano (Ver en Inicio)', url: '/descargas/tapalpa' },

  ];

  const aboutDates = [
    { label: 'Inicio de proceso de formulación', date: '07/06/2019' },
    { label: 'Inicio de Consulta Pública', date: '19/06/2020' },
    { label: 'Fin de Consulta Pública', date: '18/09/2020' },
    { label: 'Sesión de validación', date: ' 20/04/2021' },
    { label: 'Fecha Publicación', date: '20/11/2021' },
  ];
  
  const bitacoraItems = [
    {
      title: 'FASE DE FORMULACIÓN',
      icon: FileSignature,
      color: 'from-blue-500 to-cyan-500',
      content: [
        {
          subtitle: 'Actas de Aprobación Municipal',
          items: [
            { text: 'Acta de Aprobación Atemajac de Brizuela', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/tapalpa/actasaprobacion/Acta_Apro_Atemajac_Brizuela.pdf' },
            { text: 'Acta de Aprobación Chiquilistlán', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/tapalpa/actasaprobacion/Acta_Apro_Chiquilistlan.pdf' },
            { text: 'Acta de Aprobación San Gabriel', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/tapalpa/actasaprobacion/Acta_Apro_San_Gabriel.pdf' },
            { text: 'Acta de Aprobación Tapalpa', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/tapalpa/actasaprobacion/Acta_Apro_Tapalpa.pdf' }
          ]
        },
        {
          subtitle: 'Convenios y Acuerdos',
          items: [
            { text: 'Convenio de Coordinación Estado-Municipios Región Tapalpa', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/tapalpa/Convenio/01_Convenio_Estado_Municipios.pdf' },
            { text: 'Convenio Marco Estado - Federación', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/tapalpa/Convenio/02_Convenio_Marco_Estado_Federacion.pdf' },
            { text: 'Convenio Específico Estado - Federación', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/tapalpa/Convenio/03_Convenio_Específico_Estado_Federacion.pdf' }
          ]
        }
      ]
    },
    {
      title: 'FASE DE EXPEDICIÓN',
      icon: FileSpreadsheet,
      color: 'from-emerald-500 to-green-500',
      content: [
        {
          subtitle: 'Consulta Pública',
          items: [
            { text: 'Documento puesto en Consulta Pública', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Elaboracion/01_PROETDUM_RT.pdf' },
            { text: 'Respuestas Consulta Pública Tapalpa', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Elaboracion/02_Respuestas.pdf' },
            { text: 'Publicación Periódico del Estado Región Tapalpa', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Elaboracion/03_Publicacion.pdf' }
          ]
        }
      ]
    },
    {
      title: 'FASE DE EJECUCIÓN',
      icon: Zap,
      color: 'from-amber-500 to-orange-500',
      content: [
        {
          subtitle: 'Monitoreo',
          items: [
            { text: 'Boleta Registral Tapalpa', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Elaboracion/04_Boleta_Tapalpa.pdf' },
            { text: 'Boleta Registral Atemajac de Brizuela', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Elaboracion/05_Boleta_Atemajac.pdf' }
          ]
        }
      ]
    },
    {
      title: 'FASE DE EVALUACIÓN',
      icon: BarChart3,
      color: 'from-purple-500 to-violet-500',
      content: [
        {
          subtitle: 'Evaluaciones',
          items: [
            { text: 'Informes de Evaluación', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Tapalpa/Evaluacion/Informes_de_Evaluacion.zip' }
          ]
        }
      ]
    }
  ];

  return (
    <div className="environmental-theme fade-in">
      <ModernBanner onScrollClick={scrollToSection} />

      {/* Introduction Section */}
      <section id="introduccion" className="py-16 lg:py-24 bg-white dark:bg-gray-900 relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  Ordenamiento Región Tapalpa
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed text-justify">
                  El Gobierno del Estado de Jalisco impulsa el desarrollo sustentable en la región denominada "Tapalpa", integrada por los municipios de Atemajac de Brizuela, Chiquilistlán, San Gabriel y Tapalpa. Para impulsar dicho desarrollo, el día 15 de junio del año 2019, el Gobierno del Estado de Jalisco y los municipios firmaron un Convenio de Coordinación con el objetivo de realizar los instrumentos necesarios para ordenar el territorio de la región.
                </p>
                <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed text-justify">
                  Asimismo, se firmó con la Federación un Convenio Marco de Coordinación de fecha 27 de enero de 2020 y derivado del mismo un Convenio de Coordinación Específico (SEMARNAT-CONANP y SEDATU) de fecha 27 de mayo 2020. Estos convenios establecen los fundamentos para la colaboración en el proceso de elaboración de los instrumentos de ordenamiento territorial.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {aboutDates.map((item, index) => (
                <div key={index} className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-xl p-6 border border-teal-200 dark:border-teal-800">
                  <div className="text-sm text-teal-600 dark:text-teal-400 font-semibold">{item.label}</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{item.date}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* DocumentRepository2 Section */}
      <section className="py-16 lg:py-24 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <DocumentRepository2
          items={TapalpaMain}
          title="Documentos Principales"
          onDownload={(doc) => window.open(doc.url, '_blank')}
          showFooter={false}
        />
      </section>

      {/* Downloads Section */}
      <Descargas 
        dataItems={items}
        title="Descargas"
        description="Accede a los documentos, archivos y recursos del ordenamiento ecológico territorial"
      />

      {/* Municipalities Section */}
      <section className="municipalities-section py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-cyan-100 dark:from-gray-900 dark:to-gray-800 min-h-screen font-sans">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-4xl md:text-5xl font-extrabold text-teal-800 dark:text-teal-400 text-center mb-16 relative">
            <MapPin className="inline-block mr-3 mb-1 w-10 h-10" />
            Municipios de la Región: Tapalpa
            <p className="text-lg font-normal text-gray-600 dark:text-gray-400 mt-2">
              Conoce los municipios y sus características clave.
            </p>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {municipalities.map((municipality) => (
              <motion.div 
                key={municipality.id}
                onClick={() => setSelectedMunicipality(municipality)}
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                className={`
                  municipality-card 
                  rounded-2xl 
                  overflow-hidden 
                  shadow-xl 
                  transition-all 
                  duration-500 
                  cursor-pointer 
                  hover:shadow-2xl 
                  bg-white/90 dark:bg-gray-800/80
                  backdrop-blur-sm
                  border border-gray-100 dark:border-gray-700
                  ${selectedMunicipality.id === municipality.id 
                    ? 'ring-4 ring-teal-500 scale-[1.03] shadow-teal-300/50 dark:shadow-teal-700/50' 
                    : 'hover:ring-2 hover:ring-teal-300'
                  }
                `}
              >
                <div className={`h-28 ${municipality.color} flex flex-col items-center justify-center p-3 text-white font-bold`}>
                  <img 
                    src={municipality.shield} 
                    alt={`Escudo de ${municipality.name}`} 
                    className="h-14 w-14 object-contain mb-1 drop-shadow-md"
                    onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/64x64/000000/ffffff?text=E"; }}
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-xl font-extrabold text-center text-gray-900 dark:text-white">{municipality.name}</h3>
                  <p className="text-xs text-center text-teal-600 dark:text-teal-400 mt-1 flex items-center justify-center">
                    Ver Detalles <ChevronRight className='w-3 h-3 ml-1'/>
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div 
            key={selectedMunicipality.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="municipality-details bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 md:p-12 max-w-6xl mx-auto border border-teal-200 dark:border-teal-900"
          >
            <div className="flex flex-col md:flex-row items-start gap-10">
              <div className="w-full md:w-1/3 flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <img 
                  src={selectedMunicipality.shield} 
                  alt={`Escudo de ${selectedMunicipality.name}`} 
                  className="h-48 w-48 object-contain shadow-lg rounded-full p-2 bg-white dark:bg-gray-900 mb-6 border-4 border-teal-300 dark:border-teal-500"
                  onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/192x192/000000/ffffff?text=E"; }}
                />
                <h3 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white text-center">{selectedMunicipality.name}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm text-center italic mt-2">{selectedMunicipality.description}</p>
              </div>
              
              <div className="w-full md:w-2/3">
                <h4 className="text-2xl font-bold text-teal-700 dark:text-teal-400 mb-6 border-b border-gray-200 dark:border-gray-700 pb-2 flex items-center">
                  <Info className='w-5 h-5 mr-2'/> Datos Clave
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DetailBadge icon={Users} label="Población" value={selectedMunicipality.population} />
                  <DetailBadge icon={Map} label="Superficie" value={selectedMunicipality.area} />
                  <DetailBadge icon={Calendar} label="Municipalización" value={selectedMunicipality.foundation} />
                  <DetailBadge icon={Landmark} label="Región Administrativa" value={selectedMunicipality.administrativeRegion} />
                  <DetailBadge icon={MapPin} label="Número de UGAs" value={selectedMunicipality.NumerodeUgas} />
                  <DetailBadge icon={MountainSnow} label="Zonas de Importancia Ecológica" value={selectedMunicipality.EcologicaZona} />
                </div>
                
                <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3 flex items-center">
                    <Trees className='text-blue-500 mr-2 w-5 h-5'/> Principales Atracciones
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
                    {selectedMunicipality.attractions}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <FullWidthVisorWithSidebar />

      {/* Bitácora Section */}
      <ModernBitacoraSection items={bitacoraItems} />

      {/* Fases Section */}
      <FasesSection 
        imageUrl="images/FasesOrdenamiento.png"
        altText="Fases del Ordenamiento Tapalpa"
      />
    </div>
  );
};

export default TapalpaPage;